import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XIcon, CalendarIcon, ClockIcon, CheckCircleIcon, XCircleIcon, EditIcon, ArchiveIcon, UserIcon, CreditCardIcon, LandmarkIcon, WalletIcon, FileTextIcon, ShieldCheckIcon, AlertCircleIcon, DownloadIcon, SmartphoneIcon, PaperclipIcon } from 'lucide-react';
import { mockBills, approveBill, rejectBill, updateBill } from '../mocks/mockBills';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
export function BillDetail() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [, forceUpdate] = useState(0);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const bill = mockBills.find(b => b.id === id) || mockBills[0];
  const currentUser = 'Simon Verraest';
  // Look up supplier for payout details
  const supplier = mockSuppliers.find(s => s.id === bill.supplierId);
  // Derived state logic
  const isScheduled = bill.status === 'approved' && bill.approvalStatus === 'approved';
  const isDue = bill.status === 'approved' && !isScheduled;
  const isPending = bill.status === 'pending_approval';
  const isPaid = bill.status === 'paid';
  const isRejected = bill.status === 'rejected';
  const isOverdue = bill.status === 'overdue';
  const isAssignedToMe = bill.approver === currentUser;
  const displayStatus = isPaid ? 'Paid' : isRejected ? 'Rejected' : isScheduled ? 'Scheduled' : isPending ? 'Pending Approval' : isOverdue ? 'Overdue' : isDue ? 'Due' : 'Draft';
  const getStatusBadgeClass = () => {
    if (isPaid) return 'bg-green-50 text-green-700 border-green-200';
    if (isRejected || isOverdue) return 'bg-red-50 text-red-700 border-red-200';
    if (isScheduled) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (isPending) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };
  // Handlers
  const handleApprove = () => {
    approveBill(bill.id, currentUser);
    forceUpdate(n => n + 1);
  };
  const handleReject = () => {
    rejectBill(bill.id, currentUser, rejectNotes);
    setShowRejectModal(false);
    setRejectNotes('');
    forceUpdate(n => n + 1);
  };
  const handleArchive = () => {
    updateBill(bill.id, {
      status: 'rejected',
      approvalNotes: 'Archived by user'
    });
    forceUpdate(n => n + 1);
  };
  const handleCancelSchedule = () => {
    updateBill(bill.id, {
      status: 'approved',
      approvalStatus: 'pending',
      approver: undefined
    });
    forceUpdate(n => n + 1);
  };
  const formatCurrency = (amount: number) => {
    return formatCurrencyAmount(amount, {
      currency: bill.currency,
      fractionDigits: 2
    });
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  // Helper: get payout method display details from supplier
  const getPayoutDetails = () => {
    if (!supplier) return null;
    if (supplier.payoutMethod === 'bank_account') {
      return {
        type: 'Bank Account',
        icon: <LandmarkIcon size={16} className="text-gray-400" data-id="element-3503" />,
        details: [{
          label: 'Bank',
          value: supplier.bankName || '—'
        }, {
          label: 'Account',
          value: supplier.accountNumber ? '•••' + supplier.accountNumber.slice(-4) : '—'
        }, {
          label: 'Account Name',
          value: supplier.accountName || '—'
        }]
      };
    }
    return {
      type: 'PromptPay',
      icon: <SmartphoneIcon size={16} className="text-gray-400" data-id="element-3504" />,
      details: [{
        label: 'Type',
        value: supplier.promptPayType === 'phone' ? 'Phone Number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'
      }, {
        label: 'ID',
        value: supplier.promptPayId ? supplier.promptPayId.slice(0, 3) + '••••' + supplier.promptPayId.slice(-3) : '—'
      }, {
        label: 'Name',
        value: supplier.promptPayName || '—'
      }]
    };
  };
  // Helper: get payment source display
  const getPaymentSourceLabel = () => {
    switch (bill.paymentSource) {
      case 'balance':
        return 'Maybank Balance';
      case 'card':
        return 'Corporate Card';
      case 'bank_transfer':
        return 'Bank Transfer';
      default:
        return 'Unknown';
    }
  };
  const getPaymentSourceIcon = () => {
    switch (bill.paymentSource) {
      case 'balance':
        return <WalletIcon size={16} className="text-gray-400" data-id="element-3505" />;
      case 'card':
        return <CreditCardIcon size={16} className="text-gray-400" data-id="element-3506" />;
      default:
        return <LandmarkIcon size={16} className="text-gray-400" data-id="element-3507" />;
    }
  };
  // ===== TIMELINE LOGIC (corrected) =====
  // Step statuses: 'completed' | 'current' | 'future' | 'rejected'
  const approvalCompleted = bill.approvalStatus === 'approved' || isScheduled || isPaid || isDue; // isDue means status='approved', so approval happened
  const approvalRejected = bill.approvalStatus === 'rejected' || isRejected;
  const steps = [{
    id: 'created',
    label: 'Bill Created',
    date: bill.issueDate,
    actor: bill.createdBy || currentUser,
    status: 'completed' as const
  }, {
    id: 'approval',
    label: 'Approval',
    date: approvalCompleted || approvalRejected ? bill.approvalDate : undefined,
    actor: approvalCompleted ? bill.approver || 'Auto-approved' : approvalRejected ? bill.approver || 'System' : isPending ? bill.approver || 'Awaiting assignment' : '—',
    status: approvalCompleted ? 'completed' as const : approvalRejected ? 'rejected' as const : isPending ? 'current' as const : 'future' as const
  }, {
    id: 'scheduled',
    label: 'Scheduled',
    date: isScheduled || isPaid ? bill.dueDate : undefined,
    actor: isScheduled || isPaid ? 'System' : undefined,
    status: isScheduled || isPaid ? 'completed' as const : 'future' as const
  }, {
    id: 'payment',
    label: 'Payment',
    date: isPaid ? bill.paidDate : undefined,
    actor: isPaid ? getPaymentSourceLabel() : undefined,
    status: isPaid ? 'completed' as const : isScheduled ? 'current' as const : 'future' as const
  }];
  const payoutDetails = getPayoutDetails();
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-3508">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3509">
        <button onClick={() => navigate('/bills')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors" data-id="element-3510">
          <ArrowLeftIcon size={18} data-id="element-3511" />
          {t('pages.BillApprovalWizard.back_to_bills')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-3512">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2 space-y-6" data-id="element-3513">
          {/* Card 1: Header & Total */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm" data-id="element-3514">
            <div className="flex justify-between items-start mb-6" data-id="element-3515">
              <div data-id="element-3516">
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2" data-id="element-3517">
                  {bill.billNumber}
                </h1>
                <p className="text-gray-500" data-id="element-3518">
                  {bill.description || 'No description provided'}
                </p>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeClass()}`} data-id="element-3519">
                {displayStatus}
              </span>
            </div>
            <div className="pt-6 border-t border-gray-100" data-id="element-3520">
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-3521">
                {t('pages.BillDetail.total_amount')}
              </div>
              <div className="text-4xl font-bold text-gray-900" data-id="element-3522">
                {formatCurrency(bill.total)}
              </div>
            </div>
          </div>

          {/* Card: Dates */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3523">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-3524">
              <div data-id="element-3525">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3526">
                  {t('pages.BillDetail.issue_date')}
                </div>
                <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3527">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-3528" />
                  {formatDate(bill.issueDate)}
                </div>
              </div>
              <div data-id="element-3529">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3530">
                  {t('pages.BillDetail.due_date')}
                </div>
                <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3531">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-3532" />
                  {formatDate(bill.dueDate)}
                </div>
              </div>
              {bill.approvalDate && approvalCompleted && <div data-id="element-3533">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3534">
                    {t('pages.BillDetail.approval_date')}
                  </div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3535">
                    <CheckCircleIcon size={14} className="text-gray-400" data-id="element-3536" />
                    {formatDate(bill.approvalDate)}
                  </div>
                </div>}
              {bill.paidDate && <div data-id="element-3537">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3538">
                    {t('pages.BillDetail.paid_date')}
                  </div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3539">
                    <CheckCircleIcon size={14} className="text-green-500" data-id="element-3540" />
                    {formatDate(bill.paidDate)}
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Card: Supplier */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3541">
          <div className="flex justify-between items-center mb-6" data-id="element-3542">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2" data-id="element-3543">
              <UserIcon size={16} className="text-gray-400" data-id="element-3544" />
              {t('pages.BillDetail.supplier_details')}
            </h3>
            <button onClick={() => navigate(`/supplier-detail/${bill.supplierId}`)} className="text-sm font-medium text-[#B38A00] hover:text-[#8C6C00] transition-colors" data-id="element-3545">
              {t('pages.BillDetail.view_profile')}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3546">
            <div data-id="element-3547">
              <div className="text-xs text-gray-500 mb-1" data-id="element-3548">
                {t('pages.BillDetail.company_name')}
              </div>
              <div className="text-sm font-medium text-gray-900" data-id="element-3549">
                {bill.supplierName}
              </div>
            </div>
            <div data-id="element-3550">
              <div className="text-xs text-gray-500 mb-1" data-id="element-3551">
                {t('pages.BillDetail.contact_email')}
              </div>
              <div className="text-sm font-medium text-gray-900" data-id="element-3552">
                {bill.supplierEmail}
              </div>
            </div>
            {bill.supplierPhone && <div data-id="element-3553">
                <div className="text-xs text-gray-500 mb-1" data-id="element-3554">
                  {t('pages.BillDetail.phone_number')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-3555">
                  {bill.supplierPhone}
                </div>
              </div>}
          </div>
        </div>

        {/* Card: Payment Information (Paid/Scheduled) */}
        {(isPaid || isScheduled) && <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3556">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 mb-6" data-id="element-3557">
              <CreditCardIcon size={16} className="text-gray-400" data-id="element-3558" />
              {t('pages.BillDetail.payment_information')}
            </h3>

            {isPaid ? <div className="space-y-6" data-id="element-3559">
                {/* Payment Source */}
                <div data-id="element-3560">
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3" data-id="element-3561">
                    {t('pages.BillDetail.funded_from')}
                  </div>
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-3562">
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-3563">
                      {getPaymentSourceIcon()}
                    </div>
                    <div data-id="element-3564">
                      <div className="text-sm font-semibold text-gray-900" data-id="element-3565">
                        {getPaymentSourceLabel()}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-3566">
                        {t('pages.BillDetail.paid_on')}{' '}
                        {formatDate(bill.paidDate)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payout Destination */}
                {payoutDetails && <div data-id="element-3567">
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3" data-id="element-3568">
                      {t('pages.BillDetail.paid_to')}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-3569">
                      <div className="flex items-center gap-3 mb-4" data-id="element-3570">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-3571">
                          {payoutDetails.icon}
                        </div>
                        <div className="text-sm font-semibold text-gray-900" data-id="element-3572">
                          {payoutDetails.type}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200" data-id="element-3573">
                        {payoutDetails.details.map(d => <div key={d.label} data-id="element-3574">
                            <div className="text-xs text-gray-500 mb-0.5" data-id="element-3575">
                              {d.label}
                            </div>
                            <div className="text-sm font-medium text-gray-900" data-id="element-3576">
                              {d.value}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>}
              </div> : <div className="bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start gap-3 border border-blue-100" data-id="element-3577">
                <ClockIcon size={18} className="text-blue-600 mt-0.5" data-id="element-3578" />
                <div data-id="element-3579">
                  <div className="font-medium text-sm" data-id="element-3580">
                    {t('pages.BillDetail.scheduled_for_payment')}
                  </div>
                  <div className="text-xs mt-1 text-blue-700" data-id="element-3581">
                    {t('pages.BillDetail.this_bill_is_scheduled_to_be')}{' '}
                    {formatDate(bill.dueDate)}.
                  </div>
                </div>
              </div>}
          </div>}

        {/* Card: Attachment (replaces Line Items) */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3582">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 mb-6" data-id="element-3583">
            <PaperclipIcon size={16} className="text-gray-400" data-id="element-3584" />
            {t('pages.BillDetail.attachment')}
          </h3>

          {bill.attachmentFileName ? <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-3585">
              <div className="flex items-center gap-3 min-w-0" data-id="element-3586">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-3587">
                  <FileTextIcon size={20} className="text-red-500" data-id="element-3588" />
                </div>
                <div className="min-w-0" data-id="element-3589">
                  <div className="text-sm font-medium text-gray-900 truncate" data-id="element-3590">
                    {bill.attachmentFileName}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3591">
                    {t('pages.BillDetail.uploaded_with_bill')}
                  </div>
                </div>
              </div>
              <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#B38A00] hover:bg-[#FFFBE6] rounded-lg transition-colors flex-shrink-0" data-id="element-3592">
                <DownloadIcon size={16} aria-hidden="true" data-id="element-3593" />
                {t('pages.BillDetail.download')}
              </button>
            </div> : <div className="text-center py-8 text-gray-400" data-id="element-3594">
              <PaperclipIcon size={24} className="mx-auto mb-2 opacity-50" data-id="element-3595" />
              <p className="text-sm" data-id="element-3596">
                {t('pages.BillDetail.no_file_attached_to_this_bill')}
              </p>
            </div>}

          {/* Amount Summary */}
          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end" data-id="element-3597">
            <div className="w-full max-w-xs space-y-3" data-id="element-3598">
              <div className="flex justify-between text-sm" data-id="element-3599">
                <span className="text-gray-500" data-id="element-3600">
                  {t('pages.BillDetail.subtotal')}
                </span>
                <span className="text-gray-900 font-medium" data-id="element-3601">
                  {formatCurrency(bill.subtotal)}
                </span>
              </div>
              {bill.taxAmount && bill.taxAmount > 0 && <div className="flex justify-between text-sm" data-id="element-3602">
                  <span className="text-gray-500" data-id="element-3603">
                    {t('pages.BillDetail.tax')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-3604">
                    {formatCurrency(bill.taxAmount)}
                  </span>
                </div>}
              {bill.discountAmount && bill.discountAmount > 0 && <div className="flex justify-between text-sm" data-id="element-3605">
                  <span className="text-gray-500" data-id="element-3606">
                    {t('pages.BillDetail.discount')}
                  </span>
                  <span className="text-red-600 font-medium" data-id="element-3607">
                    -{formatCurrency(bill.discountAmount)}
                  </span>
                </div>}
              <div className="flex justify-between pt-3 border-t border-gray-200" data-id="element-3608">
                <span className="text-base font-bold text-gray-900" data-id="element-3609">
                  {t('pages.BillDetail.total')}
                </span>
                <span className="text-base font-bold text-gray-900" data-id="element-3610">
                  {formatCurrency(bill.total)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Card: Notes */}
        {bill.notes && <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3611">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3612">
              {t('pages.BillDetail.notes')}
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 border border-gray-100" data-id="element-3613">
              {bill.notes}
            </div>
          </div>}
      </div>

      {/* RIGHT COLUMN */}
      <div className="lg:col-span-1 space-y-6" data-id="element-3614">
        {/* ACTIONS CARD */}
        <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isPending && isAssignedToMe ? 'border-l-4 border-l-[#FFC70A] border-y-gray-200 border-r-gray-200' : 'border-gray-200'}`} data-id="element-3615">
          <div className="p-6" data-id="element-3616">
            {/* DUE */}
            {isDue && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-5" data-id="element-3617">
                  {t('pages.BillDetail.actions')}
                </h3>
                <div className="space-y-3" data-id="element-3618">
                  <button onClick={() => navigate(`/bill-approval/schedule?ids=${bill.id}`)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FFC70A] text-gray-900 rounded-lg hover:bg-[#D9A800] text-sm font-medium shadow-sm transition-colors" data-id="element-3619">
                    <CalendarIcon size={16} data-id="element-3620" />
                    {t('pages.BillDetail.schedule_payment')}
                  </button>
                  <button onClick={handleArchive} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-3621">
                    <ArchiveIcon size={16} data-id="element-3622" />
                    {t('pages.BillDetail.archive_bill')}
                  </button>
                  <button onClick={() => navigate(`/bill-form?id=${bill.id}`)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[#B38A00] hover:bg-[#FFFBE6] rounded-lg text-sm font-medium transition-colors mt-2" data-id="element-3623">
                    <EditIcon size={16} aria-hidden="true" data-id="element-3624" />
                    {t('pages.BillDetail.edit_details')}
                  </button>
                </div>
              </>}

            {/* PENDING */}
            {isPending && <>
                {isAssignedToMe ? <>
                    <div className="flex items-center gap-2 mb-3" data-id="element-3625">
                      <ShieldCheckIcon size={18} className="text-[#B38A00]" data-id="element-3626" />
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-3627">
                        {t('pages.BillDetail.approval_required')}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-6" data-id="element-3628">
                      {t('pages.BillDetail.this_bill_has_been_assigned_to')}
                    </p>
                    <div className="space-y-3" data-id="element-3629">
                      <button onClick={handleApprove} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FFC70A] text-gray-900 rounded-lg hover:bg-[#D9A800] text-sm font-medium shadow-sm transition-colors" data-id="element-3630">
                        <CheckIcon size={16} data-id="element-3631" />
                        {t('pages.BillDetail.approve_bill')}
                      </button>
                      <button onClick={() => setShowRejectModal(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 text-sm font-medium transition-colors" data-id="element-3632">
                        <XIcon size={16} data-id="element-3633" />
                        {t('pages.BillDetail.reject_bill')}
                      </button>
                    </div>
                  </> : <>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3634">
                      {t('pages.BillDetail.status')}
                    </h3>
                    <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 flex items-start gap-3" data-id="element-3635">
                      <ClockIcon size={18} className="text-yellow-600 mt-0.5" data-id="element-3636" />

                      <div data-id="element-3637">
                        <div className="text-sm font-medium text-yellow-900" data-id="element-3638">
                          {t('pages.BillDetail.awaiting_approval')}
                        </div>
                        <div className="text-xs text-yellow-700 mt-1" data-id="element-3639">
                          {t('pages.BillDetail.assigned_to')}{' '}
                          <span className="font-semibold" data-id="element-3640">
                            {bill.approver || 'Unassigned'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>}
              </>}

            {/* SCHEDULED */}
            {isScheduled && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3641">
                  {t('pages.BillDetail.status')}
                </h3>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3 mb-6" data-id="element-3642">
                  <CalendarIcon size={18} className="text-blue-600 mt-0.5" data-id="element-3643" />
                  <div data-id="element-3644">
                    <div className="text-sm font-medium text-blue-900" data-id="element-3645">
                      {t('pages.BillDetail.payment_scheduled')}
                    </div>
                    <div className="text-xs text-blue-700 mt-1" data-id="element-3646">
                      {t('pages.BillDetail.will_be_paid_on')}{' '}
                      {formatDate(bill.dueDate)}
                    </div>
                  </div>
                </div>
                <button onClick={handleCancelSchedule} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-3647">
                  <XIcon size={16} aria-hidden="true" data-id="element-3648" />
                  {t('pages.BillDetail.cancel_schedule')}
                </button>
              </>}

            {/* PAID */}
            {isPaid && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3649">
                  {t('pages.BillDetail.status')}
                </h3>
                <div className="bg-green-50 border border-green-100 rounded-lg p-4 flex items-start gap-3" data-id="element-3650">
                  <CheckCircleIcon size={18} className="text-green-600 mt-0.5" data-id="element-3651" />

                  <div data-id="element-3652">
                    <div className="text-sm font-medium text-green-900" data-id="element-3653">
                      {t('pages.BillDetail.payment_complete')}
                    </div>
                    <div className="text-xs text-green-700 mt-1" data-id="element-3654">
                      {t('pages.BillDetail.successfully_paid_on')}{' '}
                      {formatDate(bill.paidDate)}
                    </div>
                  </div>
                </div>
              </>}

            {/* REJECTED */}
            {isRejected && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3655">
                  {t('pages.BillDetail.status')}
                </h3>
                <div className="bg-red-50 border border-red-100 rounded-lg p-4 flex items-start gap-3" data-id="element-3656">
                  <XCircleIcon size={18} className="text-red-600 mt-0.5" data-id="element-3657" />
                  <div data-id="element-3658">
                    <div className="text-sm font-medium text-red-900" data-id="element-3659">
                      {t('pages.BillDetail.bill_rejected')}
                    </div>
                    <div className="text-xs text-red-700 mt-1" data-id="element-3660">
                      {t('pages.BillDetail.rejected_by')}{' '}
                      {bill.approver || 'System'}
                    </div>
                  </div>
                </div>
                {bill.approvalNotes && <div className="mt-4 text-sm text-gray-600 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-3661">
                    <span className="font-medium text-gray-900 block mb-1" data-id="element-3662">
                      {t('pages.BillDetail.reason')}
                    </span>
                    {bill.approvalNotes}
                  </div>}
              </>}
          </div>
        </div>

        {/* TIMELINE CARD */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3663">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6" data-id="element-3664">
            {t('pages.BillDetail.workflow_timeline')}
          </h3>

          <div className="space-y-0 relative" data-id="element-3665">
            {steps.map((step, index) => {
            const nextStep = steps[index + 1];
            // Line is dark only if current step is completed AND next step is also completed or current
            const lineDark = step.status === 'completed' && nextStep && (nextStep.status === 'completed' || nextStep.status === 'current');
            return <div key={step.id} className="flex gap-4 relative" data-id="element-3666">
                  {/* Connector Line */}
                  {index < steps.length - 1 && <div className={`absolute left-[11px] top-7 bottom-[-7px] w-0.5 ${lineDark ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-3667" />}

                  {/* Indicator */}
                  <div className="relative z-10 flex flex-col items-center mt-1" data-id="element-3668">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${step.status === 'completed' ? 'bg-gray-900 text-white' : step.status === 'current' ? 'bg-white border-2 border-[#FFC70A]' : step.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-gray-100 border border-gray-200'}`} data-id="element-3669">
                      {step.status === 'completed' && <CheckIcon size={12} strokeWidth={3} data-id="element-3670" />}
                      {step.status === 'current' && <div className="w-2 h-2 rounded-full bg-[#FFC70A]" data-id="element-3671" />}
                      {step.status === 'rejected' && <XIcon size={12} strokeWidth={3} data-id="element-3672" />}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="pb-8 min-w-0 flex-1" data-id="element-3673">
                    <div className="flex items-center justify-between" data-id="element-3674">
                      <span className={`text-sm font-medium ${step.status === 'future' ? 'text-gray-400' : step.status === 'rejected' ? 'text-red-700' : 'text-gray-900'}`} data-id="element-3675">
                        {step.label}
                      </span>
                      {step.date && step.status !== 'future' && <span className="text-xs text-gray-500" data-id="element-3676">
                          {formatDate(step.date)}
                        </span>}
                    </div>
                    {step.status === 'current' ? <div className="text-xs mt-1 text-[#B38A00] font-medium flex items-center gap-1" data-id="element-3677">
                        <ClockIcon size={12} data-id="element-3678" /> {t('pages.BillDetail.awaiting')}
                      </div> : step.actor && step.status !== 'future' ? <div className="text-xs mt-1 text-gray-500" data-id="element-3679">
                        {step.actor}
                      </div> : null}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div> /* Reject Modal */;
  {
    /* Reject Modal */
  }
  {
    showRejectModal && <>
        <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowRejectModal(false)} data-id="element-3680" />

        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-3681">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-3682">
            <div className="p-8" data-id="element-3683">
              <div className="flex items-center gap-3 mb-4" data-id="element-3684">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-3685">
                  <AlertCircleIcon size={20} className="text-red-600" data-id="element-3686" />
                </div>
                <h2 className="text-lg font-bold text-gray-900" data-id="element-3687">
                  {t('pages.BillDetail.reject_bill')}
                </h2>
              </div>
              <p className="text-sm text-gray-600 mb-6" data-id="element-3688">
                {t('pages.BillDetail.are_you_sure_you_want_to')}{' '}
                <span className="font-medium text-gray-900" data-id="element-3689">
                  {bill.billNumber}
                </span>
                {t('pages.BillDetail.this_action_will_notify_the_submitter')}
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3690">
                {t('pages.BillDetail.reason_for_rejection')}
              </label>
              <textarea value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} rows={3} placeholder={t('pages.BillDetail.attrs.please_provide_a_reason')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-400 transition-colors resize-none" data-id="element-3691" />

              <div className="flex justify-end gap-3 mt-8" data-id="element-3692">
                <button onClick={() => {
                setShowRejectModal(false);
                setRejectNotes('');
              }} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3693">
                  {t('pages.BillApprovalWizard.cancel')}
                </button>
                <button onClick={handleReject} disabled={!rejectNotes.trim()} className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${rejectNotes.trim() ? 'bg-red-600 hover:bg-red-700 shadow-sm' : 'bg-red-300 cursor-not-allowed'}`} data-id="element-3694">
                  {t('pages.BillDetail.reject_bill')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>;
  }
}